/* bridella-wishlist.js
 * Bridella wishlist: card hearts + toast messages
 * - Single fetch to /apps/wishlist/list to hydrate active hearts
 * - Toggle via /apps/wishlist/toggle?handle=...
 * - Bottom-center toast bubble: “Saved to Favorites” / “Removed from Favorites”
 * - Prevents click bubbling to product cards; guards against double-click
 */

(function () {
  "use strict";

  const APP = {
    base: "/apps/wishlist",
    ep: {
      list: "/apps/wishlist/list",
      toggle: "/apps/wishlist/toggle",
    },
    sel: {
      btn: '[data-br-wishlist]',
    },
    cls: {
      active: "is-active",
      pending: "is-pending",
    },
    toastDuration: 1700, // ms
  };

  // ---------- CSS injection (heart state + toast) ----------
  injectStyles(`
  .br-fave-btn{display:inline-flex;align-items:center;justify-content:center;width:36px;height:36px;border-radius:9999px;border:1px solid rgba(0,0,0,.12);background:#fff;cursor:pointer;padding:0;line-height:1}
  .br-fave-btn svg{width:18px;height:18px;display:block}
  .br-fave-btn .br-heart{fill:none;stroke:currentColor;stroke-width:2}
  .br-fave-btn.${APP.cls.active} .br-heart,
  .br-fave-btn[aria-pressed="true"] .br-heart{fill:currentColor}
  .br-fave-btn.${APP.cls.pending}{opacity:.6;pointer-events:none}

  .br-toast-wrap{position:fixed;left:50%;bottom:20px;transform:translateX(-50%);z-index:9999;display:flex;flex-direction:column;gap:8px;pointer-events:none}
  .br-toast{pointer-events:auto;background:rgba(33,33,33,.95);color:#fff;border-radius:9999px;padding:10px 14px;box-shadow:0 8px 24px rgba(0,0,0,.18);opacity:0;transform:translateY(10px);transition:opacity .18s ease, transform .18s ease;font-size:14px;line-height:1.15;display:inline-flex;align-items:center;gap:8px;white-space:nowrap}
  .br-toast--show{opacity:1;transform:translateY(0)}
  .br-toast__icon{font-size:16px;line-height:1}
  .br-toast--ok{background:rgba(22,163,74,.95)}      /* green */
  .br-toast--warn{background:rgba(234,179,8,.95)}    /* amber */
  .br-toast--err{background:rgba(220,38,38,.95)}     /* red */
  `);

  // Make (or get) toast container + ARIA live region
  const toastWrap = ensureToastWrap();

  // ---------- Init on DOM ready ----------
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  function init() {
    wireButtons();
    // Hydrate from server so hearts persist on reload
    hydrateHeartsFromServer().catch(() => {
      // Non-blocking: if this fails, UI still works after first toggle
    });
  }

  // Attach click handlers; avoid duplicates
  function wireButtons() {
    document.querySelectorAll(APP.sel.btn).forEach((btn) => {
      if (btn.dataset.brBound === "1") return;
      btn.dataset.brBound = "1";
      btn.addEventListener("click", onHeartClick, { passive: false });
    });
  }

  async function onHeartClick(e) {
    // Keep card from navigating when heart is clicked
    e.preventDefault();
    e.stopPropagation();

    const btn = e.currentTarget;
    if (!btn || btn.classList.contains(APP.cls.pending)) return;

    const handle = btn.getAttribute("data-fave-handle");
    if (!handle) return;

    try {
      setPending(btn, true);
      const url = `${APP.ep.toggle}?handle=${encodeURIComponent(handle)}&_=${Date.now()}`;
      const res = await fetch(url, {
        method: "GET",
        credentials: "same-origin",
        headers: { "Accept": "application/json" },
      });
      const data = await res.json();

      if (data && !data.error) {
        // Update the state of all buttons with this handle (if multiple on page)
        syncButtonsForHandle(handle, data.action === "added");

        // Announce
        if (data.action === "added") {
          toast("Saved to Favorites", "ok");
        } else {
          toast("Removed from Favorites", "warn");
        }

        // Broadcast a custom event for any other scripts/widgets
        document.dispatchEvent(new CustomEvent("br:wishlist-change", {
          detail: { handle, action: data.action, count: data.count, handles: data.handles || [] }
        }));
      } else {
        const msg = (data && data.message) || "Unable to update favorites";
        if (msg.toLowerCase().includes("login")) {
          toast("Please sign in to use Favorites", "warn");
        } else if (msg.toLowerCase().includes("signature")) {
          // Signature issues should be rare now; surface gently
          toast("Store connection issue. Please refresh.", "err");
        } else {
          toast(msg, "err");
        }
      }
    } catch (err) {
      toast("Network error. Try again.", "err");
    } finally {
      setPending(btn, false);
    }
  }

  // Pull current favorites from server; mark hearts
  async function hydrateHeartsFromServer() {
    const res = await fetch(`${APP.ep.list}?_=${Date.now()}`, {
      method: "GET",
      credentials: "same-origin",
      headers: { "Accept": "application/json" },
    });
    const data = await res.json();
    if (!data || data.error || !Array.isArray(data.handles)) return;

    const set = new Set(data.handles);
    document.querySelectorAll(APP.sel.btn).forEach((btn) => {
      const h = btn.getAttribute("data-fave-handle");
      setActive(btn, set.has(h));
    });
  }

  function syncButtonsForHandle(handle, isActive) {
    document.querySelectorAll(APP.sel.btn).forEach((btn) => {
      if (btn.getAttribute("data-fave-handle") === handle) {
        setActive(btn, isActive);
      }
    });
  }

  function setActive(btn, on) {
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    btn.classList.toggle(APP.cls.active, !!on);
  }

  function setPending(btn, on) {
    btn.classList.toggle(APP.cls.pending, !!on);
  }

  // -------- Toasts --------
  function ensureToastWrap() {
    let el = document.querySelector(".br-toast-wrap");
    if (!el) {
      el = document.createElement("div");
      el.className = "br-toast-wrap";
      el.setAttribute("role", "status");
      el.setAttribute("aria-live", "polite");
      document.body.appendChild(el);
    }
    return el;
  }

  function toast(message, level = "ok") {
    const el = document.createElement("div");
    el.className = `br-toast br-toast--${level}`;
    const icon = document.createElement("span");
    icon.className = "br-toast__icon";
    icon.textContent = level === "ok" ? "♥" : (level === "warn" ? "!" : "×");
    const text = document.createElement("span");
    text.textContent = message;

    el.appendChild(icon);
    el.appendChild(text);
    toastWrap.appendChild(el);

    // show
    requestAnimationFrame(() => el.classList.add("br-toast--show"));

    // hide & remove
    setTimeout(() => {
      el.classList.remove("br-toast--show");
      setTimeout(() => el.remove(), 220);
    }, APP.toastDuration);
  }

  // -------- Utils --------
  function injectStyles(cssText) {
    const id = "br-wishlist-inline-styles";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.type = "text/css";
    style.appendChild(document.createTextNode(cssText));
    document.head.appendChild(style);
  }
})();
